import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

const titleStyle = {
  fontSize: { xs: '1.8rem', md: '2.8rem' },
  fontWeight: 800,
  lineHeight: 1.15,
  color: '#f8fafc',
};

const bulletStyle = {
  color: '#e2e8f0',
  fontSize: { xs: '1.05rem', md: '1.28rem' },
  lineHeight: 1.5,
  fontWeight: 500,
  mb: 1.2,
};

const panelBase = {
  borderRadius: '16px',
  border: '1px solid',
  p: { xs: 2.2, md: 3.2 },
  minHeight: { xs: 'auto', md: 360 },
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.18) 50%, transparent 75%)',
    transform: 'translateX(-130%)',
    opacity: 0,
    transition: 'transform 850ms ease, opacity 350ms ease',
    pointerEvents: 'none',
  },
  '&:hover::after': {
    transform: 'translateX(130%)',
    opacity: 0.3,
  },
};

function ResilientLogo({ alt, sources, size = 120 }) {
  const [index, setIndex] = React.useState(0);
  const current = sources[index];

  if (!current) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          border: '1px solid rgba(148,163,184,0.6)',
          color: '#e2e8f0',
          fontWeight: 800,
          fontSize: 14,
        }}
      >
        {alt.slice(0, 2).toUpperCase()}
      </Box>
    );
  }

  return (
    <img
      src={current}
      alt={alt}
      onError={() => setIndex((prev) => prev + 1)}
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  );
}

function EyesenseFineTuningPlaybook() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        px: { xs: 2, md: 5 },
        py: { xs: 3, md: 5 },
        background:
          'radial-gradient(circle at 10% 8%, rgba(56,189,248,0.18), transparent 34%), radial-gradient(circle at 86% 10%, rgba(14,165,233,0.2), transparent 36%), linear-gradient(180deg, #020617 0%, #0b1530 100%)',
      }}
    >
      <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ResilientLogo
              alt="Pomsoft"
              size={140}
              sources={[
                '/pomsoft_dark_background.png',
                'https://eyesense.ai/pomsoft_dark_background.png',
                'https://pomsoft.net/images/pomsoft_dark_background.png',
              ]}
            />
            <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.5rem' } }}>
              Pomsoft
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <ResilientLogo
              alt="EyeSense.ai"
              size={140}
              sources={[
                '/eyesense-logo-v2-tight.png',
                '/eyesense-logo-v2.png',
                'https://eyesense.ai/eyesense-logo-v2-tight.png',
                'https://eyesense.ai/eyesense-logo-v2.png',
              ]}
            />
            <Typography sx={{ color: '#f8fafc', fontWeight: 700, fontSize: { xs: '1.5rem', md: '2.5rem' } }}>
              EyeSense.ai
            </Typography>
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={titleStyle}>Eyesense.ai Fine-Tuning Playbook</Typography>
          <Typography
            sx={{
              color: '#e2e8f0',
              fontSize: { xs: '1.1rem', md: '1.6rem' },
              mt: 1.2,
              fontWeight: 500,
            }}
          >
            How human curation and LLM curation work together to continuously improve detection quality.
          </Typography>
        </Box>

        <Box
          sx={{
            height: 4,
            borderRadius: 999,
            background: 'linear-gradient(90deg, #38bdf8 0%, #22d3ee 100%)',
            mb: 3.5,
          }}
        />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            mb: 3.5,
          }}
        >
          <Box
            sx={{
              ...panelBase,
              borderColor: 'rgba(34,211,238,0.65)',
              boxShadow: '0 0 24px rgba(34,211,238,0.2)',
              background:
                'radial-gradient(circle at 15% 50%, rgba(34,211,238,0.16), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.78) 0%, rgba(2,6,23,0.62) 100%)',
            }}
          >
            <Typography sx={{ color: '#22d3ee', fontSize: '1.85rem', fontWeight: 800, mb: 1.8 }}>
              Human-Curated Fine-Tuning
            </Typography>
            <Typography sx={bulletStyle}>- Operators mark bad detections (veto) directly from real camera history.</Typography>
            <Typography sx={bulletStyle}>- Confirmed detections become positive examples; vetoed detections become background/negative context.</Typography>
            <Typography sx={bulletStyle}>- Model versions are explicitly managed, with rollback and activation controls.</Typography>
            <Typography sx={bulletStyle}>- This creates an auditable training loop grounded in real deployment conditions.</Typography>
          </Box>

          <Box
            sx={{
              ...panelBase,
              borderColor: 'rgba(125,211,252,0.65)',
              boxShadow: '0 0 24px rgba(125,211,252,0.18)',
              background:
                'radial-gradient(circle at 85% 50%, rgba(56,189,248,0.15), transparent 45%), linear-gradient(180deg, rgba(2,6,23,0.8) 0%, rgba(2,6,23,0.64) 100%)',
            }}
          >
            <Typography sx={{ color: '#7dd3fc', fontSize: '1.85rem', fontWeight: 800, mb: 1.8 }}>
              LLM-Curated Fine-Tuning
            </Typography>
            <Typography sx={bulletStyle}>- LLM validation gates ambiguous detections and classifies likely false positives.</Typography>
            <Typography sx={bulletStyle}>- Rejected detections are collected as high-value hard negatives for retraining.</Typography>
            <Typography sx={bulletStyle}>- This reduces annotation burden while widening coverage of corner cases.</Typography>
            <Typography sx={bulletStyle}>- Human review remains the override for policy and quality control.</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            borderRadius: '14px',
            border: '1px solid rgba(148,163,184,0.45)',
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.82) 0%, rgba(30,41,59,0.72) 50%, rgba(2,6,23,0.82) 100%)',
            py: { xs: 2.2, md: 2.8 },
            px: { xs: 2, md: 3.5 },
            textAlign: 'center',
            mb: 3.5,
            position: 'relative',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(110deg, transparent 25%, rgba(255,255,255,0.16) 50%, transparent 75%)',
              transform: 'translateX(-130%)',
              opacity: 0,
              transition: 'transform 850ms ease, opacity 350ms ease',
              pointerEvents: 'none',
            },
            '&:hover::after': {
              transform: 'translateX(130%)',
              opacity: 0.3,
            },
          }}
        >
          <Typography sx={{ color: '#f8fafc', fontSize: { xs: '1.2rem', md: '1.8rem' }, fontWeight: 600, lineHeight: 1.35 }}>
            Best results come from combining both loops:
          </Typography>
          <Typography sx={{ color: '#22d3ee', fontSize: { xs: '1.2rem', md: '1.8rem' }, fontWeight: 700, lineHeight: 1.35 }}>
            LLM scales triage, humans define correctness.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.2,
            mb: 3,
            borderRadius: '12px',
            p: 1,
            background:
              'linear-gradient(90deg, rgba(2,6,23,0.72) 0%, rgba(30,41,59,0.6) 50%, rgba(2,6,23,0.72) 100%)',
            border: '1px solid rgba(148,163,184,0.35)',
          }}
        >
          <Chip label="Detection History → Curation" sx={{ bgcolor: '#22d3ee', color: '#082f49', fontWeight: 700 }} />
          <Chip label="Curation → Training" sx={{ bgcolor: '#38bdf8', color: '#082f49', fontWeight: 700 }} />
          <Chip label="Training → ONNX Export" sx={{ bgcolor: '#0ea5e9', color: '#e0f2fe', fontWeight: 700 }} />
          <Chip label="Version Activation + Rollback" sx={{ bgcolor: '#1e293b', color: '#f8fafc', fontWeight: 700 }} />
        </Box>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography sx={{ color: '#e2e8f0', fontSize: { xs: '1rem', md: '1.2rem' } }}>
            Eyesense.ai continuous improvement: real-world detections → smarter models → safer automation.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default EyesenseFineTuningPlaybook;
